import {
  ActivityModel,
  AddressModel,
  DocumentModel,
  ErrorModel,
  SubscriptionModel,
  UrlContentObjArrayModel,
  UserProfileEnum,
  PersonStatus,
} from '../../../common/src';
import {
  ConfigModel,
  ConfigWebUserModel,
  PaymentProvidersConfigModel,
  PaymentProvidersEnum,
} from '../../../common/src/models/config.model';
import store from '@/store';
import { passwordStrength, Options } from 'check-password-strength';
import moment from 'moment';
import i18n, { Language } from '@/i18n';
import router from '@/router';
import UserService from '@/services/user.service';
import ConfigurationService from '@/services/configuration.service';
import { redirectUrl } from '@/shared/route-utils';
import DOMPurify, { type Config } from 'dompurify';
import stateList from '@/assets/state-list.json';
import countryBasedZipCodeFormats from '@/assets/zipcode-format.json';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const CLASS_DEFAULT_IMAGE = '/default_class_3.png';

export class FormField {
  value?: any;
  error?: string;
  checking?: boolean;
  options?: any[];
}

export class FormFields {
  constructor(
    public subscriptionStartDate: FormField = {},
    public email: FormField = {},
    public confirmEmail: FormField = {},
    public password: FormField = {},
    public confirmPassword: FormField = {},
    public salutation: FormField = {},
    public gender: FormField = {},
    public firstName: FormField = {},
    public lastName: FormField = {},
    public birthday: FormField = {},
    public phone: FormField = {},
    public idType: FormField = {},
    public idNumber: FormField = {},
    public passportDate: FormField = {},
    public passportCountry: FormField = {},
    public address1: FormField = {},
    public address2: FormField = {},
    public zipCode: FormField = {},
    public city: FormField = {},
    public country: FormField = {},
    public state: FormField = { options: stateList },
    public ssn: FormField = {},
    public terms: FormField = { value: false },
    public checkbox1: FormField = { value: false },
    public checkbox2: FormField = { value: false },
    public checkbox3: FormField = { value: false },
    public language: FormField = {},
    public allowThirdPartyOffers: FormField = { value: false },
    public allowEmailNewsletters: FormField = { value: false }
  ) {}
}

export function getInvalidFields(formElements: any) {
  let invalidField: string | undefined = undefined;
  for (const key in formElements) {
    if ((formElements as any)[key].error || (formElements as any)[key].checking) {
      invalidField = key;
      break;
    }
  }
  return invalidField;
}

// Utility function to handle redirect and sign out logic
export function handleSignOut(userService: UserService, configurationService: ConfigurationService, params?: Record<string, string>): void {
  const redirect = store.state.config?.redirects.logout || '/login';
  // No redirect
  if (redirect === '/login') {
    router.push({ name: 'Login', query: params }).then(() => {
      userService.signOut();
      configurationService.loadConfiguration(true);
    });
  } else {
    /* istanbul ignore next */
    if (params && params.sessionTimeoutError === 'general.sessionTimeoutError') {
      const sessionTimeoutErrorKey: string = 'general.sessionTimeoutError';
      alert(i18n.global.t(sessionTimeoutErrorKey));
    }
    store.commit('config', undefined);
    userService.signOut();
    redirectUrl(redirect);
  }
}

export function getLookAndFeel(config: ConfigModel | undefined, param: any): string | null {
  // The case where the banner is not set cannot be tested for now.
  // TODO: if there's a page without a banner, it needs to be tested
  /* istanbul ignore next */
  if (!config || !config.lookAndFeel || !param) return null;
  /* istanbul ignore next */
  return (config.lookAndFeel as any)[String(param)] || null;
}

/**
 * Check if an email is valid
 * @param email User email
 * @returns Boolean
 */
export function validateEmail(email: string): boolean {
  /* Same regex that is used in Exerp to validate email */
  const regex = /^[\w!#$%&'*+=?`{|}~^-]+(?:\.[\w!#$%&'*+=?`{|}~^-]+)*@(?:[a-zA-Z\d-]+\.){1,63}[a-zA-Z]{2,63}$/;
  return regex.test(email);
}

export function downloadDocument(documentModel: DocumentModel) {
  const sliceSize = 1024;
  let byteCharacters;
  try {
    byteCharacters = atob(documentModel.data);
  } catch (e) {
    throw new ErrorModel('general.documentFormatError');
  }
  const bytesLength = byteCharacters.length;
  const slicesCount = Math.ceil(bytesLength / sliceSize);
  const byteArrays = new Array(slicesCount);
  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    const begin = sliceIndex * sliceSize;
    const end = Math.min(begin + sliceSize, bytesLength);
    const bytes = new Array(end - begin);
    for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  const blob = new Blob(byteArrays, { type: documentModel.type });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = documentModel.name + '';
  link.click();
}

export function isNullOrUndefined(object: any): boolean {
  return object === null || object === undefined;
}

// get the max between a few dates. The first one must not be null or undefined
export function getMaxDate(date: Date, ...date1: (Date | undefined)[]): Date {
  // sort dates (at least the first date is an actual date and not undefined)
  const dates = [date, ...date1].filter(d => d !== undefined).sort((a, b) => a!.getTime() - b!.getTime());
  return dates[dates.length - 1]!; // at least one date is defined, the others were filtered out
}

export function getPasswordStrength(password: string): { strength: number; text: string; cssClass: string } {
  const modifiedOptions: Options<string> = [
    {
      id: 0,
      value: 'Too weak',
      minDiversity: 0,
      minLength: 0,
    },
    {
      id: 1,
      value: 'Weak',
      minDiversity: 3,
      minLength: 8,
    },
    {
      id: 2,
      value: 'Medium',
      minDiversity: 4,
      minLength: 8,
    },
    {
      id: 3,
      value: 'Strong',
      minDiversity: 4,
      minLength: 10,
    },
  ];
  const strength = passwordStrength(password, modifiedOptions).id;

  let text;
  let cssClass;
  if (strength < 1) {
    text = 'subscription.tooWeak';
    cssClass = 'text-red-500';
  } else if (strength < 2) {
    text = 'subscription.weak';
    cssClass = 'text-yellow-600';
  } else if (strength < 3) {
    text = 'subscription.medium';
    cssClass = 'text-blue-500';
  } else {
    text = 'subscription.strong';
    cssClass = 'text-green-500';
  }
  return { strength, text, cssClass };
}

export function getClassImage(activity: ActivityModel): string {
  return (
    store.state.config!.lookAndFeel.classImages[activity.id] ||
    (activity.name && store.state.config!.lookAndFeel.classImages[activity.name.trim().toLowerCase()]) ||
    store.state.config!.lookAndFeel.classImages['default'] ||
    CLASS_DEFAULT_IMAGE
  );
}

export function getAddonImage(addonName: string | undefined) {
  if (!addonName) return store.state.config!.lookAndFeel.addonImages['default'];

  const formattedAddonName = addonName.trim().toLowerCase();
  return store.state.config!.lookAndFeel.addonImages[formattedAddonName] || store.state.config!.lookAndFeel.addonImages['default'];
}

export function formatAmountToLocale(amount: string | number, currency: string | undefined): string {
  if (!currency || String(currency) === 'undefined') {
    currency = 'XXX';
  }
  const display = new Intl.NumberFormat(store.state.currentLocale!.numberFormat, {
    style: 'currency',
    currency,
  }).format(Number(amount || 0));
  return display.replace('XXX', '').replace('¤', '').trim(); //remove ¤ in safari
}

export function capitalizeFirstLetter(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function isWrrongCenter(centerId: number) {
  if (!centerId) {
    return true;
  }
  return store.state.user && !store.state.config!.features.allowSubscriptionSaleOutsideHomeCenter && centerId !== store.state.user.centerId;
}

// This should only be used for joinFlow, Baf, Weblead, and new subscription (Not for the profile)
// in the profile, config.minimumAge is not used and will always be undefined, so that's cool
export function getMinimumDateForCreationNewPerson(config: ConfigWebUserModel) {
  if (config.minimumAge) {
    return moment().subtract(config.minimumAge, 'years').format('YYYY-MM-DD');
  } else {
    return getToday();
  }
}

export function getToday() {
  return moment().format('YYYY-MM-DD');
}

export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export function checkIfStaffActingForMember(selectedMemberId: number | undefined): boolean {
  return (
    store.state.user!.accessLevel.includes(UserProfileEnum.StaffLevel) &&
    selectedMemberId !== undefined &&
    Number(selectedMemberId) !== Number(store.state.user!.userId) &&
    !isUserARelation(Number(selectedMemberId))
  );
}

function isUserARelation(memberId: number | undefined): boolean {
  const relations = store.state.user!.relations;
  return relations.findIndex(user => user.userId === memberId) > -1;
}

export function staffIsAllowedToPay(listOfPaymentProviders: PaymentProvidersEnum[]): boolean {
  let returnedValue = false;
  for (const paymentProvider of listOfPaymentProviders) {
    if (store.state.config!.paymentProvidersConfig[paymentProvider]!.allowedForStaff) {
      returnedValue = true;
      break;
    }
  }
  return returnedValue;
}

export function filterPaymentProviderListByProfile(
  paymentProvidersConfig: PaymentProvidersConfigModel,
  paymentProviderList: PaymentProvidersEnum[],
  selectedMemberId: number | false
): PaymentProvidersEnum[] {
  const filteredListOfPaymentProviders: PaymentProvidersEnum[] = [];

  // Check first if the member is a staff member
  for (const paymentProvider of paymentProviderList) {
    if (selectedMemberId === false) {
      filteredListOfPaymentProviders.push(paymentProvider);
    } else if (paymentProvidersConfig[paymentProvider]!.allowedForStaff && checkIfStaffActingForMember(selectedMemberId)) {
      filteredListOfPaymentProviders.push(paymentProvider);
    } else if (paymentProvidersConfig[paymentProvider]!.allowedForMember && !checkIfStaffActingForMember(selectedMemberId)) {
      filteredListOfPaymentProviders.push(paymentProvider);
    }
  }

  return filteredListOfPaymentProviders;
}

export function toTitleCase(input: string): string {
  if (input) {
    const convertedCase = input.replace(/(\p{L}+)/gu, match => match.charAt(0).toUpperCase() + match.slice(1));
    return convertedCase;
  }
  return '';
}

export function getLanguage(code?: string): Language {
  let result: Language = store.state.availableLocales[0];
  store.state.availableLocales.forEach(lang => {
    if (lang.code === code) {
      result = lang;
    }
  });
  return result;
}

export function floorNumberToPrecision(number: number, precision: number): number {
  return Math.floor(number * Math.pow(10, precision)) / Math.pow(10, precision);
}

export interface TabElement {
  id: string;
  name: string;
  label: string;
  icon: string;
  condition?: () => Promise<boolean>;
}
// AddressModel toString
export function addressToString(address: AddressModel): string {
  return (
    (address.address1 ? address.address1 + ', ' : '') +
    (address.address2 ? address.address2 + ', ' : '') +
    (address.address3 ? address.address3 + ', ' : '') +
    (address.zip ? address.zip + ' ' : '') +
    (address.zipName ? address.zipName + ', ' : '') +
    (address.country ? address.country : '')
  );
}

export function blurAllFormElements(element: HTMLElement) {
  // in IOS when the popup is open the date time selection is automatically focused and the calendar opens:
  const formElements = element.querySelectorAll('input, select, checkbox');
  for (const formElement of formElements) {
    (formElement as HTMLInputElement | HTMLSelectElement).blur();
  }
}

/**
 * ##### Attribute white listing needed #####
 * Some html attributes like 'target' may be stripped off and needs white listing if config is passed.
 *
 * If config is empty 'target' attribute will be white listed by default
 */
export function sanitizeHtml(html: string, config?: Config): string {
  const allowedAttributes = ['target'];
  if (!config) {
    config = {
      ADD_ATTR: allowedAttributes, // allow whitelisted attributes if no config is provided
    };
  }

  return DOMPurify.sanitize(html, config);
}

export function filterOutContentByUrl(url: string, currentRoute: string): boolean {
  const trimmedUrl = url.trim();

  // Exact route matching
  if (trimmedUrl.endsWith('$')) {
    const exactRoute = trimmedUrl.slice(0, -1);
    return currentRoute === exactRoute;
  }

  // Partial route matching
  const normalizedUrl = trimmedUrl.endsWith('/') ? trimmedUrl.slice(0, -1) : trimmedUrl;
  return currentRoute.toLowerCase().includes(normalizedUrl.toLowerCase());
}

export function getZipCodeFormat(countryISOCode: string, fallback: string): string {
  const systemProperty = store.state.config?.zipCodeList.zipCodeList;
  if (systemProperty && systemProperty[countryISOCode]) {
    return (systemProperty[countryISOCode] as { Country: string; Format: string }).Format;
  }
  const zipCodeFormats = countryBasedZipCodeFormats as Record<string, { Country: string; Format: string }>;
  return zipCodeFormats[countryISOCode] ? zipCodeFormats[countryISOCode].Format : fallback;
}

export const ADDRESS_FIELD_MAX_LENGTH = 70; // database has limit of 70 for address length

export function isUserActive(userId: string) {
  const ACTIVE_USER_STATUS = ['ACTIVE', 'TEMPORARYINACTIVE'];
  return ACTIVE_USER_STATUS.includes(userId);
}

export function getUserActionsLimitedInDebtSysProps() {
  return store.state.config!.features.limitUserActionsWhenInOverdueDebt?.split(',') || [];
}

export function isHTML(value: string) {
  const document = new DOMParser().parseFromString(value, 'text/html');
  const hasHtml = Array.from(document.body.childNodes).some(node => node.nodeType === 1); // Node.ELEMENT_NODE eg: p, a, div tags
  return hasHtml;
}

export function isRCCSubWithAvailableSize(subscription: SubscriptionModel) {
  return subscription.subscriptionType === 'RECURRING_CLIPCARD' && subscription.availableSizes && subscription.availableSizes.length > 0;
}

export function getNumberOfDaysBetweenTwoDates(date1: string, date2: string) {
  return moment(date1, 'YYYY-MM-DD').diff(moment(date2, 'YYYY-MM-DD'), 'days');
}

export function evaluateStraplineConditions(conditions: any): boolean {
  if (!conditions) {
    return true;
  }

  const user = store.state.user;
  if (!user) {
    return false;
  }

  if (conditions.memberStatus && Array.isArray(conditions.memberStatus)) {
    const userStatus: PersonStatus | undefined = user.status;
    const requiredStatuses = conditions.memberStatus.map((status: string) => status.toUpperCase());

    if (userStatus && requiredStatuses.includes(userStatus)) {
      return true;
    }

    return false;
  }

  return false;
}

export function getPaywithTranslationWithProivider(payWithCase: string, provider: string) {
  if (provider === 'odin') {
    /*
        odin is changed to exper pay, but the sys prop names are still referring to odin.
        So this is a small fix to handle in a small scale than making changes to the provider
        name since it still referes to odin the system property
      */
    provider = 'exerp pay';
  }
  return i18n.global.t(payWithCase, { provider });
}

export function usableUrl(url?: string): string | undefined {
  try {
    const parsed = new URL(url ?? '');
    return parsed.protocol === 'https:' ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}
